// ============================================================================
// Wissensdatenbank — pure Helfer (ohne DOM/React testbar)
// ----------------------------------------------------------------------------
// Root Cause des Leerzustands (Befund 2026-06-10): Die Seite las überall
// `res.data.items`, die Knowledge-API liefert aber DIREKTE Arrays — dazu
// falsche Feldnamen (model statt modelName, peakPower statt nominalPowerWp,
// pricePerKwh statt priceCtPerKwh). Hier: tolerantes Unwrapping + Mapping
// auf die echten API-Shapes (Component-Tabelle = dieselbe Quelle wie der
// Stringing-Selector; der Kosten-Katalog ist BEWUSST getrennt — Stage-1-
// Kalkulationskatalog, siehe modules/costing/price-catalog.ts).
// ============================================================================

/** API liefert Arrays direkt; defensiv auch {items:[...]} akzeptieren. */
export function unwrapList<T>(data: unknown): T[] {
  if (Array.isArray(data)) return data as T[];
  if (data && typeof data === "object" && Array.isArray((data as { items?: unknown }).items)) {
    return (data as { items: T[] }).items;
  }
  return [];
}

export interface KnowledgeComponent {
  id: string;
  manufacturer: string;
  modelName: string;
  type: string;
  dataQuality?: string | null;   // verified | sample | unverified
  isVerified?: boolean | null;
  dataSource?: string | null;
  dataVersion?: string | null;
  specs?: Record<string, unknown> | null;
}

export const COMPONENT_TYPE_LABELS: Record<string, string> = {
  pv_module: "PV-Modul",
  inverter: "Wechselrichter",
  battery: "Speicher",
  wallbox: "Wallbox",
};

export function componentTypeLabel(type: string): string {
  return COMPONENT_TYPE_LABELS[type] ?? type;
}

const num = (v: unknown): number | null => (typeof v === "number" && isFinite(v) ? v : null);

/** Typ-spezifische Schlüssel-Specs als kompakte Zeile (ehrlich: nur was da ist). */
export function keySpecsFor(type: string, specs: Record<string, unknown> | null | undefined): string {
  if (!specs) return "—";
  const parts: string[] = [];
  if (type === "pv_module") {
    const wp = num(specs.nominalPowerWp);
    const voc = num(specs.vocV);
    const tk = num(specs.tempCoeffVoc);
    if (wp != null) parts.push(`${wp} Wp`);
    if (voc != null) parts.push(`Voc ${voc} V`);
    if (tk != null) parts.push(`${tk} %/K`);
  } else if (type === "inverter") {
    const kw = num(specs.nominalPowerKw);
    const maxV = num(specs.maxInputVoltageV);
    const mpptMin = num(specs.mpptVoltageMinV);
    const mpptMax = num(specs.mpptVoltageMaxV);
    if (kw != null) parts.push(`${kw} kW`);
    if (maxV != null) parts.push(`max ${maxV} V DC`);
    if (mpptMin != null && mpptMax != null) parts.push(`MPPT ${mpptMin}–${mpptMax} V`);
  } else if (type === "battery") {
    const kwh = num(specs.capacityKwh) ?? num(specs.nominalCapacityKwh);
    if (kwh != null) parts.push(`${kwh} kWh`);
  }
  return parts.length > 0 ? parts.join(" · ") : "—";
}

export interface QualityBadge {
  label: string;
  tone: "ok" | "warn" | "muted";
}

/** Ehrliche Qualitäts-Kennzeichnung — niemals Verifikation vortäuschen. */
export function qualityBadgeFor(c: Pick<KnowledgeComponent, "dataQuality" | "isVerified">): QualityBadge {
  if (c.dataQuality === "sample") return { label: "MUSTER", tone: "warn" };
  if (c.dataQuality === "verified" && c.isVerified) return { label: "verifiziert (lt. Quelle)", tone: "ok" };
  return { label: "ungeprüft", tone: "muted" };
}

/** Provenienz-Zeile (dataSource + Stand), ehrlich leer wenn unbekannt. */
export function provenanceFor(c: Pick<KnowledgeComponent, "dataSource" | "dataVersion">): string {
  const parts = [c.dataSource, c.dataVersion ? `Stand ${c.dataVersion}` : null].filter(Boolean);
  return parts.length > 0 ? parts.join(" · ") : "Quelle unbekannt";
}

/** Leertext je Kategorie-Filter — ehrlich statt generisch kaputt. */
export function emptyTextFor(typeFilter: string): string {
  if (!typeFilter) return "Noch keine Komponenten gepflegt.";
  const label = COMPONENT_TYPE_LABELS[typeFilter] ?? typeFilter;
  return `Noch keine Komponenten der Kategorie ${label} gepflegt.`;
}

// Echte API-Shapes der weiteren Tabs (knowledge.data.ts)
export interface KnowledgeTariff {
  id: string;
  name: string;
  priceCtPerKwh: number;
  gridFeeCtPerKwh?: number;
  taxesCtPerKwh?: number;
  totalCtPerKwh?: number;
  note?: string;
}

export interface KnowledgeSubsidy {
  id: string;
  name: string;
  maxEur: number;
  percentOfInvestment: number;
  region: string;
  eligibility?: string;
  validUntil?: string;
  website?: string;
  note?: string;
}

export interface KnowledgeGridOperator {
  name: string;
  region: string;
  website?: string;
}
